import { type RefObject, useEffect, useState } from "react";

type GrabToScrollOptions = {
  removeScrollBar?: boolean;
  grabCursor?: boolean;
  acceleration?: number;
  accelerationX?: number;
  accelerationY?: number;
};

export const useGrabToScroll = (
  ref: RefObject<HTMLElement>,
  options?: GrabToScrollOptions
) => {
  const {
    acceleration,
    accelerationX,
    accelerationY,
    grabCursor,
    removeScrollBar,
  } = options ?? {};

  const [isDown, setIsDown] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);

  useEffect(() => {
    const elm = ref.current;

    if (!elm) return;

    if (removeScrollBar) {
      elm.classList.add("no-scrollbar");
    }

    const handleMouseDown = (e: MouseEvent) => {
      setIsDown(true);
      setStartX(e.pageX - elm.offsetLeft);
      setStartY(e.pageY - elm.offsetTop);
      setScrollLeft(elm.scrollLeft + 1);
      setScrollTop(elm.scrollTop + 1);
      if (grabCursor) {
        elm.style.cursor = "grabbing";
      }
    };

    const handleMouseLeave = () => {
      setIsDown(false);
    };

    const handleMouseUp = () => {
      setIsDown(false);
      if (grabCursor) {
        elm.style.removeProperty("cursor");
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDown) return;
      e.preventDefault();

      const x = e.pageX - elm.offsetLeft;
      const walkX = (x - startX) * (accelerationX ?? acceleration ?? 1); //scroll-fast
      elm.scrollLeft = scrollLeft - walkX;

      const y = e.pageY - elm.offsetTop;
      const walkY = (y - startY) * (accelerationY ?? acceleration ?? 1); //scroll-fast
      elm.scrollTop = scrollTop - walkY;
    };

    elm.addEventListener("mousedown", handleMouseDown);
    elm.addEventListener("mouseleave", handleMouseLeave);
    elm.addEventListener("mouseup", handleMouseUp);
    elm.addEventListener("mousemove", handleMouseMove);

    return () => {
      elm.removeEventListener("mousedown", handleMouseDown);
      elm.removeEventListener("mouseleave", handleMouseLeave);
      elm.removeEventListener("mouseup", handleMouseUp);
      elm.removeEventListener("mousemove", handleMouseMove);
    };
  }, [
    ref,
    isDown,
    scrollLeft,
    scrollTop,
    startX,
    startY,
    acceleration,
    accelerationX,
    accelerationY,
    grabCursor,
    removeScrollBar,
  ]);
};
